import type {
  OptionGroup,
  OptionId,
  Product,
  SelectedOptions,
} from "@/configurator/domain/models/types";

// Every group kind except multi-select `gridOptions`, i.e. the ones whose
// selection is a single option id.
export type SingleSelectGroup = Exclude<OptionGroup, { kind: "gridOptions" }>;

/**
 * Currently selected ids of a group, as a list: single-select groups resolve to
 * one id, multi-select `gridOptions` to many. Falls back to the group default
 * while the user has not touched the group.
 */
export function getSelectedOptionIds(
  group: OptionGroup,
  selected: SelectedOptions,
): OptionId[] {
  const value = selected[group.id] ?? group.defaultOptionId;
  return Array.isArray(value) ? value : [value];
}

/** Same fallback for the single-select kinds, where callers need one id. */
export function getSelectedOptionId(
  group: SingleSelectGroup,
  selected: SelectedOptions,
): OptionId {
  const value = selected[group.id];
  return typeof value === "string" ? value : group.defaultOptionId;
}

/**
 * The selection a product starts from: every group at its configured default.
 */
export function getDefaultSelectedOptions(
  product: Product | undefined,
): SelectedOptions {
  if (!product) return {};
  return Object.fromEntries(
    product.optionGroups.map((group) => [group.id, group.defaultOptionId]),
  ) as SelectedOptions;
}

function hasOption(group: OptionGroup, optionId: OptionId): boolean {
  return group.options.some((option) => option.id === optionId);
}

/**
 * Keeps only the groups and option ids that exist in the product, so a
 * hand-edited or stale share link cannot put the store into a state the catalog
 * cannot render. Missing groups keep their defaults.
 */
export function mergeSharedOptions(
  product: Product | undefined,
  shared: SelectedOptions,
): SelectedOptions {
  const defaults = getDefaultSelectedOptions(product);
  if (!product) return defaults;

  const merged = { ...defaults };

  for (const group of product.optionGroups) {
    const value = shared[group.id];
    if (value === undefined) continue;

    if (group.kind === "gridOptions") {
      merged[group.id] = (Array.isArray(value) ? value : [value]).filter((id) =>
        hasOption(group, id),
      );
      continue;
    }

    const id = Array.isArray(value) ? value[0] : value;
    if (id && hasOption(group, id)) merged[group.id] = id;
  }

  return merged;
}

/** The selection after toggling one option of a multi-select group. */
export function toggleSelectedOption(
  selected: SelectedOptions,
  groupId: OptionGroup["id"],
  optionId: OptionId,
): SelectedOptions {
  const current = selected[groupId];
  const list = Array.isArray(current) ? current : [];
  const next = list.includes(optionId)
    ? list.filter((id) => id !== optionId)
    : [...list, optionId];

  return { ...selected, [groupId]: next };
}
